import { useMemo } from "react";
import { queries } from "../../../shared/queries";
import { useQuery } from "../../hooks/useQuery";
import { useSpaceDisplayName } from "../../hooks/useSpaceDisplayName";
import { spaceIcon } from "../../lib/spaceIcon";
import { Icon, type IconName } from "../Icon";
import { Text } from "../Text";
import {
  FormCard,
  IconContainer,
  RowLabel,
  SectionHeading,
  SpendList,
  SpendRow,
  UsdInput,
} from "./modalForm";

export type LimitsDraft = Record<
  string,
  {
    value: string;
    kind: "total" | "space";
    budgetId?: string;
    warnThresholdPct?: number;
  }
>;

const inputStyle = { width: 160 } as const;

type Row = {
  id: string;
  label: string;
  icon: IconName;
  kind: "total" | "space";
  budgetId?: string;
  baseline: string;
  warnThresholdPct?: number;
};

type Props = {
  providerId: string;
  limitsDraft: LimitsDraft;
  disabled: boolean;
  onChange: (next: LimitsDraft) => void;
};

export function AiProviderSpendLimitsSection({
  providerId,
  limitsDraft,
  disabled,
  onChange,
}: Props) {
  const [allSpaces] = useQuery(
    queries.spacesLite({ includeAdminSpaces: true })
  );
  const [budgets] = useQuery(queries.monitoringAiBudgets());
  const displayName = useSpaceDisplayName();

  const rows = useMemo<Row[]>(() => {
    const total = (budgets ?? []).find(
      (budget) =>
        budget.scopeKind === "ai_provider" && budget.scopeId === providerId
    );
    const spaceBudgets = new Map(
      (budgets ?? [])
        .filter(
          (budget) =>
            budget.scopeKind === "space" && budget.aiProviderId === providerId
        )
        .map((budget) => [budget.scopeId, budget] as const)
    );
    const spaceRows = (allSpaces ?? [])
      .filter((space) => space.mode !== "personal")
      .map((space) => {
        const budget = spaceBudgets.get(space.id);
        return {
          id: space.id,
          label: displayName(space),
          icon: spaceIcon(space.mode),
          kind: "space" as const,
          budgetId: budget?.id,
          baseline:
            budget?.limitAmount == null ? "" : String(budget.limitAmount),
        };
      })
      .sort((a, b) => a.label.localeCompare(b.label));
    return [
      {
        id: "total",
        label: "Total spend",
        icon: "wallet",
        kind: "total" as const,
        budgetId: total?.id,
        baseline: total?.limitAmount == null ? "" : String(total.limitAmount),
        warnThresholdPct: total?.warnThresholdPct ?? undefined,
      },
      ...spaceRows,
    ];
  }, [allSpaces, budgets, displayName, providerId]);

  const valueFor = (row: Row) => limitsDraft[row.id]?.value ?? row.baseline;

  const setValue = (row: Row, value: string) =>
    onChange({
      ...limitsDraft,
      [row.id]: {
        value,
        kind: row.kind,
        budgetId: row.budgetId,
        warnThresholdPct: row.warnThresholdPct,
      },
    });

  return (
    <FormCard>
      <SectionHeading
        title="Spend notifications"
        description="Warns in Monitoring when this provider’s spend over the last 30 days reaches an amount — for the total and per space. Empty means no notification; requests are never blocked."
      />
      <SpendList>
        {rows.map((row) => (
          <SpendRow key={row.id}>
            <IconContainer>
              <Icon name={row.icon} size={16} />
            </IconContainer>
            <RowLabel>
              <Text
                variant="text-regular-md"
                color="var(--text-primary)"
                ellipsis
              >
                {row.label}
              </Text>
            </RowLabel>
            <UsdInput
              value={valueFor(row)}
              placeholder="0.00"
              disabled={disabled}
              onChange={(next) => setValue(row, next)}
              style={inputStyle}
            />
          </SpendRow>
        ))}
      </SpendList>
    </FormCard>
  );
}
